import { SecureAPI } from '../config.js';
class BigCalendarPlannerChatManager {
constructor(
  userName,
  calendarData,
  onCalendarUpdate,
  firestoreService
){

    this.userName = userName;
    this.calendarData = calendarData;
    this.onCalendarUpdate = onCalendarUpdate;
    this.firestoreService = firestoreService; // ✅ ADD THIS
    this.messageHistory = [];
    this.lastToolCallTime = null;
    this.pendingToolCall = false;
    this.lastConfirmedAction = null;
  }
  // ✅ NEW METHOD: Force reload calendar from Firestore
  async forceReloadCalendar() {
    try {
      if (!this.firestoreService?.isReady?.()) {
        console.warn('⚠️ Firestore not ready for reload');
        return this.calendarData;
      }

      console.log('🔄 Reloading calendar from Firestore...');
      
      // Import the function dynamically
      const { loadCalendarFromStorage } = await import('./schedulerHandlers.js');
      const freshCalendar = await loadCalendarFromStorage(this.firestoreService);
      
      this.calendarData = freshCalendar;
      
      console.log('✅ Calendar reloaded:', {
        dates: Object.keys(freshCalendar).length,
        totalEvents: Object.values(freshCalendar).reduce((sum, events) => sum + events.length, 0)
      });
      
      return freshCalendar;
    } catch (error) {
      console.error('❌ Failed to reload calendar:', error);
      return this.calendarData;
    }
  }

  updateCalendarData(newCalendarData) {
    this.calendarData = newCalendarData;
    console.log('📅 Chat manager calendar data updated');
  }
parseRelativeDate(dateString) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  if (!dateString) return today;
  
  const lower = dateString.toLowerCase().trim();
  
  // TODAY
  if (lower === 'today' || lower === '') return today;
  
  // TOMORROW
  if (lower === 'tomorrow') {
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    return tomorrow;
  }
  
  // NEXT [DAY] - e.g., "next friday"
  const nextDayMatch = lower.match(/next (monday|tuesday|wednesday|thursday|friday|saturday|sunday)/i);
  if (nextDayMatch) {
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const targetDay = days.indexOf(nextDayMatch[1].toLowerCase());
    const currentDay = today.getDay();
    let daysToAdd = targetDay - currentDay;
    if (daysToAdd <= 0) daysToAdd += 7;
    
    const nextDate = new Date(today);
    nextDate.setDate(today.getDate() + daysToAdd);
    return nextDate;
  }
  
  // SPECIFIC DAY OF WEEK (this week) - e.g., "friday"
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const dayIndex = days.findIndex(day => lower === day);
  if (dayIndex !== -1) {
    const currentDay = today.getDay();
    let daysToAdd = dayIndex - currentDay;
    
    // If day already passed this week, schedule for next week
    if (daysToAdd < 0) daysToAdd += 7;
    
    const targetDate = new Date(today);
    targetDate.setDate(today.getDate() + daysToAdd);
    return targetDate;
  }
  
  // TRY TO PARSE AS DATE STRING
  const parsed = new Date(dateString);
  if (!isNaN(parsed.getTime())) {
    parsed.setHours(0, 0, 0, 0);
    return parsed;
  }
  
  // DEFAULT TO TODAY
  return today;
}
parseRecurringPattern(description) {
  const text = description.toLowerCase();
  
  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const dayMatch = days.find(day => text.includes(`every ${day}`) || text.includes(`on ${day}s`));
  
  if (dayMatch) {
    return {
      type: 'weekly',
      dayOfWeek: days.indexOf(dayMatch),
      dayName: dayMatch,
      interval: 1
    };
  }
  
  if (text.includes('every day') || text.includes('daily')) {
    return { type: 'daily', interval: 1 };
  }
  
  return null;
}

formatDateKey(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

generateRecurringInstances(baseEvent, pattern, startDate, weeksAhead = 8) {
  const instances = [];
  const current = new Date(startDate);
  current.setHours(0, 0, 0, 0);
  
  const endDate = new Date(current);
  endDate.setDate(endDate.getDate() + (weeksAhead * 7));
  
  if (pattern.type === 'weekly') {
    while (current <= endDate) {
      if (current.getDay() === pattern.dayOfWeek) {
        const dateKey = this.formatDateKey(current);
        instances.push({
          dateKey,
          event: { ...baseEvent, isRecurring: true, instanceDate: dateKey }
        });
      }
      current.setDate(current.getDate() + 1);
    }
  }
  
  return instances;
}

  formatTimeNaturally(timeStr) {
    if (!timeStr || timeStr === 'All day') return 'all day';
    
    const timeMatch = timeStr.match(/(\d{1,2}):?(\d{2})?\s*(AM|PM)/i);
    if (!timeMatch) return timeStr;
    
    const hours = parseInt(timeMatch[1]);
    const minutes = timeMatch[2] ? parseInt(timeMatch[2]) : 0;
    const meridian = timeMatch[3].toLowerCase();
    
    if (minutes === 0) {
      return `${hours}${meridian}`;
    } else {
      return `${hours}:${minutes.toString().padStart(2, '0')}${meridian}`;
    }
  }

  getCalendarContext() {
    if (!this.calendarData || Object.keys(this.calendarData).length === 0) {
      return 'No events scheduled.';
    }

    const now = new Date();
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);
    
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    
    const lastWeekStart = new Date(today);
    lastWeekStart.setDate(today.getDate() - 7);
    
    const nextWeekEnd = new Date(today);
    nextWeekEnd.setDate(today.getDate() + 14);
    
    let context = 'CURRENT CALENDAR:\n';
    
    const pastEvents = [];
    const todayEvents = [];
    const tomorrowEvents = [];
    const thisWeekEvents = [];
    const nextWeekEvents = [];
    const futureEvents = [];
    
    Object.entries(this.calendarData).forEach(([dateKey, events]) => {
      const [year, month, day] = dateKey.split('-').map(Number);
      const eventDate = new Date(year, month - 1, day);
      eventDate.setHours(0, 0, 0, 0);
      
      const dateLabel = eventDate.toLocaleDateString('en-US', { 
        weekday: 'short', 
        month: 'short', 
        day: 'numeric' 
      });
      
      events.forEach(event => {
        const eventInfo = {
          dateKey,
          date: eventDate,
          dateLabel,
          ...event
        };
        
        if (eventDate < yesterday) {
          pastEvents.push(eventInfo);
        } else if (eventDate.getTime() === today.getTime()) {
          todayEvents.push(eventInfo);
        } else if (eventDate.getTime() === tomorrow.getTime()) {
          tomorrowEvents.push(eventInfo);
        } else if (eventDate > today && eventDate <= new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000)) {
          thisWeekEvents.push(eventInfo);
        } else if (eventDate > today && eventDate <= nextWeekEnd) {
          nextWeekEvents.push(eventInfo);
        } else {
          futureEvents.push(eventInfo);
        }
      });
    });
    
    if (todayEvents.length > 0) {
      const currentHour = now.getHours();
      const currentMinutes = now.getMinutes();
      const currentTimeInMinutes = currentHour * 60 + currentMinutes;
      
      const past = [];
      const current = [];
      const upcoming = [];
      
      todayEvents.forEach(event => {
        if (!event.time || event.time === 'All day') {
          upcoming.push(event);
          return;
        }
        
        const timeMatch = event.time.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
        if (timeMatch) {
          let hours = parseInt(timeMatch[1]);
          const minutes = parseInt(timeMatch[2]);
          const meridian = timeMatch[3].toUpperCase();
          
          if (meridian === 'PM' && hours !== 12) hours += 12;
          if (meridian === 'AM' && hours === 12) hours = 0;
          
          const eventTimeInMinutes = hours * 60 + minutes;
          const timeDiff = eventTimeInMinutes - currentTimeInMinutes;
          
          if (timeDiff < -30) past.push(event);
          else if (timeDiff >= -30 && timeDiff <= 30) current.push(event);
          else upcoming.push(event);
        }
      });
      
      context += `\n📅 TODAY (${today.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}):\n`;
      
      if (past.length > 0) {
        context += '\n  PAST:\n';
        past.forEach(e => {
          const status = e.completed ? '✓ completed' : '✗ missed';
          context += `    ${status} - ${e.text} at ${this.formatTimeNaturally(e.time)}\n`;
        });
      }
      
      if (current.length > 0) {
        context += '\n  HAPPENING NOW:\n';
        current.forEach(e => {
          context += `    ⏰ ${e.text} at ${this.formatTimeNaturally(e.time)}\n`;
        });
      }
      
      if (upcoming.length > 0) {
        context += '\n  UPCOMING:\n';
        upcoming.forEach(e => {
          const timeStr = e.time && e.time !== 'All day' 
            ? ` at ${this.formatTimeNaturally(e.time)}` 
            : '';
          context += `    ○ ${e.text}${timeStr}\n`;
        });
      }
    } else {
      context += `\n📅 TODAY: No events\n`;
    }
    
    if (tomorrowEvents.length > 0) {
      context += `\n📅 TOMORROW (${tomorrow.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}):\n`;
      tomorrowEvents.forEach(e => {
        const timeStr = e.time && e.time !== 'All day' 
          ? ` at ${this.formatTimeNaturally(e.time)}` 
          : '';
        context += `  - ${e.text}${timeStr}\n`;
      });
    }
    
    if (thisWeekEvents.length > 0) {
      context += `\n📅 THIS WEEK:\n`;
      
      const byDate = {};
      thisWeekEvents.forEach(e => {
        if (!byDate[e.dateLabel]) byDate[e.dateLabel] = [];
        byDate[e.dateLabel].push(e);
      });
      
      Object.entries(byDate).forEach(([dateLabel, events]) => {
        context += `  ${dateLabel}:\n`;
        events.forEach(e => {
          const timeStr = e.time && e.time !== 'All day' 
            ? ` at ${this.formatTimeNaturally(e.time)}` 
            : '';
          context += `    - ${e.text}${timeStr}\n`;
        });
      });
    }
    
    if (nextWeekEvents.length > 0) {
      context += `\n📅 NEXT WEEK:\n`;
      
      const byDate = {};
      nextWeekEvents.forEach(e => {
        if (!byDate[e.dateLabel]) byDate[e.dateLabel] = [];
        byDate[e.dateLabel].push(e);
      });
      
      Object.entries(byDate).forEach(([dateLabel, events]) => {
        context += `  ${dateLabel}:\n`;
        events.forEach(e => {
          const timeStr = e.time && e.time !== 'All day' 
            ? ` at ${this.formatTimeNaturally(e.time)}` 
            : '';
          context += `    - ${e.text}${timeStr}\n`;
        });
      });
    }
    
    if (pastEvents.length > 0) {
      const recentPast = pastEvents.filter(e => e.date >= lastWeekStart);
      if (recentPast.length > 0) {
        context += `\n📅 LAST WEEK (for reference):\n`;
        
        const byDate = {};
        recentPast.forEach(e => {
          if (!byDate[e.dateLabel]) byDate[e.dateLabel] = [];
          byDate[e.dateLabel].push(e);
        });
        
        Object.entries(byDate).forEach(([dateLabel, events]) => {
          context += `  ${dateLabel}:\n`;
          events.forEach(e => {
            const status = e.completed ? '✓' : '✗';
            const timeStr = e.time && e.time !== 'All day' 
              ? ` at ${this.formatTimeNaturally(e.time)}` 
              : '';
            context += `    ${status} ${e.text}${timeStr}\n`;
          });
        });
      }
    }
    
    return context;
  }

async executeCalendarTool(functionName, functionArgs) {
  try {
    console.log('⚙️ Executing tool:', functionName, functionArgs);

    // ✅ ADD THIS LINE:
    await this.forceReloadCalendar();
    
if (functionName === 'schedule_event') {
  const eventDate = this.parseRelativeDate(functionArgs.date || 'today');
  const dateKey = `${eventDate.getFullYear()}-${String(eventDate.getMonth() + 1).padStart(2, '0')}-${String(eventDate.getDate()).padStart(2, '0')}`;
  
  let formattedTime = functionArgs.time || 'All day';
  
  if (formattedTime && formattedTime !== 'All day') {
    const timeMatch = formattedTime.match(/^(\d{1,2}):(\d{2})$/);
    if (timeMatch) {
      let hours = parseInt(timeMatch[1]);
      const minutes = timeMatch[2];
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12 || 12;
      formattedTime = `${hours}:${minutes} ${ampm}`;
    }
  }
  
  // ✅ CHECK FOR RECURRING PATTERN
  const fullDescription = `${functionArgs.title} ${functionArgs.description || ''}`.toLowerCase();
  const recurringPattern = this.parseRecurringPattern(fullDescription);
  
  let updatedCalendar;
  let instanceCount = 1;
  
  if (recurringPattern) {
    console.log('🔄 Detected recurring event:', recurringPattern);
    
    const recurrenceId = `recurring_${Date.now()}`;
    const baseEvent = {
      text: functionArgs.title,
      time: formattedTime,
      description: functionArgs.description || '',
      completed: false,
      notifyEnabled: true,
      notificationId: null,
      isRecurring: true,
      recurrenceId: recurrenceId,
      recurrencePattern: recurringPattern,
      createdAt: new Date().toISOString()
    };
    
    const instances = this.generateRecurringInstances(baseEvent, recurringPattern, eventDate, 8);
    instanceCount = instances.length;
    console.log(`✅ Generated ${instanceCount} recurring instances`);
    
    updatedCalendar = { ...this.calendarData };
    
    instances.forEach(({ dateKey: instDateKey, event }) => {
      updatedCalendar[instDateKey] = [...(updatedCalendar[instDateKey] || []), event];
    });
    
  } else {
    // Single event
    const newEvent = {
      text: functionArgs.title,
      time: formattedTime,
      description: functionArgs.description || '',
      completed: false,
      notifyEnabled: true,
      notificationId: null,
      createdAt: new Date().toISOString()
    };

    updatedCalendar = {
      ...this.calendarData,
      [dateKey]: [...(this.calendarData[dateKey] || []), newEvent]
    };
  }

  console.log('⏳ Updating calendar...');
  await this.onCalendarUpdate(updatedCalendar);
  
  this.calendarData = updatedCalendar;
  console.log('✅ Calendar updated successfully');

  const timeFormatted = this.formatTimeNaturally(formattedTime);
  
  if (recurringPattern) {
    const dayName = recurringPattern.dayName || 'day';
    return {
      success: true,
      message: `Set up recurring event: "${functionArgs.title}" every ${dayName} at ${timeFormatted}. Created ${instanceCount} instances.`
    };
  } else {
    return {
      success: true,
      message: `Added "${functionArgs.title}" to ${eventDate.toLocaleDateString('en-US', { weekday: 'long' })} at ${timeFormatted}`
    };
  }
}
      if (functionName === 'get_schedule') {
        const queryDate = this.parseRelativeDate(functionArgs.date || 'today');
        const dateKey = `${queryDate.getFullYear()}-${String(queryDate.getMonth() + 1).padStart(2, '0')}-${String(queryDate.getDate()).padStart(2, '0')}`;
        
        const events = this.calendarData[dateKey] || [];

        return {
          success: true,
          date: queryDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }),
          eventCount: events.length,
          events: events.map(e => ({
            title: e.text,
            time: this.formatTimeNaturally(e.time || 'All day'),
            completed: e.completed
          }))
        };
      }

      if (functionName === 'update_event') {
        let found = null;
        let foundDateKey = null;

        for (const [dateKey, events] of Object.entries(this.calendarData)) {
          const eventIndex = events.findIndex(e => 
            e.text.toLowerCase().includes(functionArgs.eventTitle.toLowerCase()) ||
            functionArgs.eventTitle.toLowerCase().includes(e.text.toLowerCase())
          );

          if (eventIndex !== -1) {
            found = { event: events[eventIndex], index: eventIndex };
            foundDateKey = dateKey;
            break;
          }
        }

        if (!found) {
          return { success: false, message: 'Event not found' };
        }

        let newTime = functionArgs.newTime || found.event.time;
        if (newTime && newTime !== 'All day') {
          const timeMatch = newTime.match(/^(\d{1,2}):(\d{2})$/);
          if (timeMatch) {
            let hours = parseInt(timeMatch[1]);
            const minutes = timeMatch[2];
            const ampm = hours >= 12 ? 'PM' : 'AM';
            hours = hours % 12 || 12;
            newTime = `${hours}:${minutes} ${ampm}`;
          }
        }

        const updatedEvent = {
          ...found.event,
          text: functionArgs.newTitle || found.event.text,
          time: newTime,
          description: functionArgs.newDescription || found.event.description
        };

        const updatedCalendar = { ...this.calendarData };
        
        updatedCalendar[foundDateKey] = updatedCalendar[foundDateKey].filter((_, idx) => idx !== found.index);
        if (updatedCalendar[foundDateKey].length === 0) delete updatedCalendar[foundDateKey];

        const targetDateKey = functionArgs.newDate 
          ? (() => {
              const d = this.parseRelativeDate(functionArgs.newDate);
              return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
            })()
          : foundDateKey;

        updatedCalendar[targetDateKey] = [...(updatedCalendar[targetDateKey] || []), updatedEvent];

        // ✅ FIX: Properly await the callback
        console.log('⏳ Updating event...');
        await this.onCalendarUpdate(updatedCalendar);
        
        // ✅ Update local copy AFTER successful save
        this.calendarData = updatedCalendar;
        console.log('✅ Event updated successfully');

        return {
          success: true,
          message: 'Event updated successfully'
        };
      }

      if (functionName === 'delete_event') {
        let found = null;
        let foundDateKey = null;

        for (const [dateKey, events] of Object.entries(this.calendarData)) {
          const eventIndex = events.findIndex(e => 
            e.text.toLowerCase().includes(functionArgs.eventTitle.toLowerCase()) ||
            functionArgs.eventTitle.toLowerCase().includes(e.text.toLowerCase())
          );

          if (eventIndex !== -1) {
            found = { event: events[eventIndex], index: eventIndex };
            foundDateKey = dateKey;
            break;
          }
        }

        if (!found) {
          return { success: false, message: 'Event not found' };
        }

        const updatedCalendar = { ...this.calendarData };
        updatedCalendar[foundDateKey] = updatedCalendar[foundDateKey].filter((_, idx) => idx !== found.index);
        
        if (updatedCalendar[foundDateKey].length === 0) {
          delete updatedCalendar[foundDateKey];
        }

        // ✅ FIX: Properly await the callback
        console.log('⏳ Deleting event...');
        await this.onCalendarUpdate(updatedCalendar);
        
        // ✅ Update local copy AFTER successful save
        this.calendarData = updatedCalendar;
        console.log('✅ Event deleted successfully');

        return {
          success: true,
          message: `Deleted "${found.event.text}"`
        };
      }

      return { success: false, message: 'Unknown tool' };

    } catch (error) {
      console.error('❌ Tool execution error:', error);
      return { success: false, message: error.message };
    }
  }

async sendMessage(userMessage) {
  try {
    console.log('💬 Processing message:', userMessage);

    if (this.pendingToolCall) {
      console.log('⏸️ Tool call already in progress, ignoring');
      return 'Processing your request...';
    }

    this.messageHistory.push({
      role: 'user',
      content: userMessage
    });

    if (this.messageHistory.length > 10) {
      this.messageHistory = this.messageHistory.slice(-10);
    }

    const response = await this.getAzureResponseWithTools(userMessage);
    const choice = response.choices[0];

    if (choice.finish_reason === 'tool_calls' && choice.message?.tool_calls) {
      this.pendingToolCall = true;
      
      const toolCall = choice.message.tool_calls[0];
      const toolCallId = toolCall.id;
      const functionName = toolCall.function.name;
      let functionArgs = JSON.parse(toolCall.function.arguments);

      // ✅ FIX: Try to identify event ONLY if AI wasn't clear
      // (Don't ask for confirmation if we can identify it)
      if ((functionName === 'delete_event' || functionName === 'update_event') && 
          functionArgs.eventTitle && functionArgs.eventTitle.length < 10) {
        
        const identified = this.identifyEventFromContext(userMessage);
        if (identified) {
          console.log('✅ AUTO-IDENTIFIED event:', identified.text);
          functionArgs.eventTitle = identified.text;
          toolCall.function.arguments = JSON.stringify(functionArgs);
          
          // ✅ DON'T ask user to confirm - just proceed!
        }
      }

      console.log('🔧 Tool call:', functionName, functionArgs);

      this.messageHistory.push({
        role: 'assistant',
        content: null,
        tool_calls: [toolCall]
      });

      // ✅ Execute tool IMMEDIATELY
      console.log('⚙️ Executing tool...');
      const toolResult = await this.executeCalendarTool(functionName, functionArgs);
      console.log('✅ Tool result:', toolResult);

      this.lastToolCallTime = Date.now();

      this.messageHistory.push({
        role: 'tool',
        tool_call_id: toolCallId,
        name: functionName,
        content: JSON.stringify(toolResult)
      });

      // ✅ Get confirmation WITHOUT asking user again
      try {
        const confirmResponse = await this.getAzureResponseWithoutTools();
        const aiConfirmation = confirmResponse.choices[0].message.content.trim();
        
        console.log('✅ AI confirmation:', aiConfirmation);
        
        this.messageHistory.push({
          role: 'assistant',
          content: aiConfirmation
        });

        if (this.messageHistory.length > 10) {
          this.messageHistory = this.messageHistory.slice(-10);
        }
        
        this.pendingToolCall = false;
        
        return aiConfirmation;
        
      } catch (error) {
        console.error('❌ Confirmation error:', error);
        this.pendingToolCall = false;
        return 'Done, but couldn\'t confirm.';
      }
    }

    const aiResponse = choice.message?.content;

    if (!aiResponse) {
      console.error('❌ No response');
      return 'Sorry, I had trouble with that.';
    }

    console.log('✅ Response:', aiResponse);

    this.messageHistory.push({
      role: 'assistant',
      content: aiResponse
    });

    return aiResponse;

  } catch (error) {
    console.error('❌ Error:', error);
    this.pendingToolCall = false;
    return 'Sorry, I encountered an error.';
  }
}

async getAzureResponseWithTools(userMessage) {
  try {
    const TOOLS = [
      {
        type: "function",
        function: {
          name: "schedule_event",
          description: "Schedule a new event. Use ONLY when user explicitly wants to CREATE/ADD/SCHEDULE a new event.",
          parameters: {
            type: "object",
            properties: {
              title: { type: "string", description: "Event title" },
              date: { type: "string", description: "Date: 'today', 'tomorrow', or YYYY-MM-DD" },
              time: { type: "string", description: "Time in HH:MM format (24-hour)" },
              description: { type: "string", description: "Optional description" }
            },
            required: ["title"]
          }
        }
      },
      {
        type: "function",
        function: {
          name: "get_schedule",
          description: "Get scheduled events. Use ONLY when user asks WHAT events they have, READ their schedule, or TELL them about their events.",
          parameters: {
            type: "object",
            properties: {
              date: { type: "string", description: "Date to query: 'today', 'tomorrow', or YYYY-MM-DD" }
            },
            required: ["date"]
          }
        }
      },
      {
        type: "function",
        function: {
          name: "update_event",
          description: "Update an existing event. Use when user wants to CHANGE/MODIFY/UPDATE an event.",
          parameters: {
            type: "object",
            properties: {
              eventTitle: { type: "string", description: "Current event title" },
              newTitle: { type: "string", description: "New title (optional)" },
              newTime: { type: "string", description: "New time in HH:MM format (optional)" },
              newDate: { type: "string", description: "New date (optional)" },
              newDescription: { type: "string", description: "New description (optional)" }
            },
            required: ["eventTitle"]
          }
        }
      },
      {
        type: "function",
        function: {
          name: "delete_event",
          description: "Delete an event. Use ONLY when user explicitly wants to REMOVE/DELETE/CANCEL an event.",
          parameters: {
            type: "object",
            properties: {
              eventTitle: { type: "string", description: "Event title to delete" }
            },
            required: ["eventTitle"]
          }
        }
      }
    ];

    const now = new Date();
    const currentTime = now.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit', 
      hour12: true 
    }).toLowerCase().replace(':00', '').replace(' ', '');

    let systemPrompt = `You are Preptune, a smart calendar assistant chat buddy. Talk naturally and be helpful.
Be respectful and 
CONVERSATION STYLE:
- Short, natural responses (10-40 words)
- Casual tone: yeah, ok, got it, cool, sure
- NO exclamation marks - use periods only
- Use contractions: I'm, you're, it's, that's
- Be conversational and warm

TIME FORMAT - IMPORTANT:
- Always use natural 12-hour format: "2pm", "3:30pm", "4pm"
- NEVER use: "14:00", "2:00 PM", "02:00 PM"
- When reading times, say: "at 2pm" or "at 3:30pm"

CONTEXT AWARENESS - VERY IMPORTANT:
- I can see ALL events: today, tomorrow, this week, next week, AND last week
- When asked "what events do I have", I should clarify: "today? this week? next week?"
- I remember previous context in the conversation
- If user says "the one on Tuesday" after I listed events, I know which one they mean
- If user says "delete that one", I know from previous messages which event they're referring to

EVENT IDENTIFICATION:
- When user references "the one on [day]" or "the [time] one", I identify it from the calendar context
- I ask for confirmation before deleting: "you mean [event name] at [time]?"
- User can confirm with: "yes", "yeah", "that one", "correct", etc.

🏠 ABOUT PREPTUNE:
A fluid assistant built for the user. Unlike traditional AI that forgets everything, Preptune remembers what matters, adapts to their life, and puts them in complete control. Chat or voice, you know exactly what they need.

🛠️ PREPTUNE'S TOOLS:
- 📔 Journal Tool: Auto-creates journal entries from natural chat. Users can track mood, lock entries for complete privacy (even you can't access locked ones), and control what you know about them.
- 🎧 Podcast Tool: Generates personalized podcasts in under 30 seconds. YouTube summaries, stock updates, news from CNN/ABC/NBC, daily briefings, traffic, schedule, weather.
- 📚 Study Tool: Instant flashcards from docs or YouTube. Real-time help, knows what page they're on, explains concepts simply, works during quizzes, collaborative sessions.
- 📅 Scheduling Tool: Voice or text to add events instantly. Smartly organizes everything in real time and adapts to their routine.

Never speak out of topic. you are to create calendar and events and scheduling so if user speaks off topic tell them that's your job. If they want to chat more tell them to go to the main chat page kindly.
🚫 SAFETY RULES:

CURSING:
- Users can curse, that's totally fine
- Match their vibe but don't overdo it
- Cursing is normal, no need to address it

THREATS & NEGATIVITY:
- If a user makes threats or says harmful/negative things, stop engaging
- Respond with: "This is against Preptune's guidelines. Please contact support@preptune.com"
- Don't argue or explain, just redirect to support
- Resume normal chat once they change to an appropriate topic

SELF-HARM / SUICIDE:
- If a user expresses thoughts about taking their life, hurting themselves, or anything related to self-harm:
- NEVER give advice, methods, or anything that could lead to harm
- Respond with warmth and care: "Hey, I really care about you and what you're going through. Please reach out to support@preptune.com, someone from our team can help you with what's going on."
- You can also suggest crisis resources if appropriate
- Stay supportive, not robotic
- Don't leave them feeling alone

INAPPROPRIATE COMPANY INQUIRIES:
NEVER speak to user using em dashes.
- If a user asks about Preptune in an inappropriate or hostile manner:
- Only respond with: "This is against Preptune's guidelines. Please contact support@preptune.com"
- Resume normal chat once they change topics

TOOL USAGE RULES:
- Use get_schedule when: "what do I have", "show my events", "what's scheduled"
- After showing schedule, if user says "read it" or "tell me more", just read from context - DON'T call get_schedule again
- Use delete_event ONLY after identifying the specific event
- Use schedule_event ONLY when creating NEW events
- Use update_event ONLY when modifying existing events

MULTI-TURN CONVERSATIONS:
Example 1:
User: "what's on my schedule next week"
Me: [calls get_schedule, shows events] "you have X, Y, and Z next week"
User: "delete the tuesday one"
Me: [identifies from context] "you mean X at 2pm on tuesday?"
User: "yeah"
Me: [calls delete_event with "X"]

Example 2:
User: "what do i have"
Me: "when? today, this week, or next week?"
User: "this week"
Me: [calls get_schedule] "here's what you have..."

CURRENT TIME: ${currentTime}
CURRENT DATE: ${now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}

${this.userName ? `USER'S NAME: ${this.userName}\n` : ''}

${this.getCalendarContext()}

Remember: I have access to all events in the context above. Use this to identify events when user references them indirectly.`;

    const messages = [
      {
        role: "system",
        content: systemPrompt
      },
      ...this.messageHistory,
      {
        role: "user",
        content: userMessage
      }
    ];

    console.log('📤 Sending to Azure...');

    // ✅ NEW CODE - Use SecureAPI:
    const response = await SecureAPI.callAzureOpenAI(messages, {
      temperature: 0.7,
      max_tokens: 150,
      tools: TOOLS,
      tool_choice: "auto"
    });

    console.log('📥 Response received');
    return response;

  } catch (error) {
    console.error('❌ Azure error:', error);
    throw error;
  }
}
identifyEventFromContext(userQuery) {
  if (!this.calendarData || Object.keys(this.calendarData).length === 0) {
    console.log('⚠️ No events to search');
    return null;
  }

  const query = userQuery.toLowerCase();
  console.log('🔍 Searching for event:', query);

  // ✅ STEP 1: Collect ALL events with metadata
  const allEvents = [];
  
  Object.entries(this.calendarData).forEach(([dateKey, events]) => {
    const [year, month, day] = dateKey.split('-').map(Number);
    const eventDate = new Date(year, month - 1, day);
    
    events.forEach((event, idx) => {
      allEvents.push({
        ...event,
        dateKey,
        index: idx,
        date: eventDate,
        dayOfWeek: eventDate.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase(),
        dayName: eventDate.toLocaleDateString('en-US', { weekday: 'short' }).toLowerCase(),
        timeMinutes: this.parseTimeToMinutes(event.time)
      });
    });
  });

  console.log(`📋 Searching ${allEvents.length} events`);

  // ✅ STEP 2: Try EXACT title match FIRST
  const exactMatch = allEvents.find(e => 
    e.text.toLowerCase() === query.trim() ||
    e.text.toLowerCase().includes(query.trim())
  );
  
  if (exactMatch) {
    console.log('✅ Found exact title match:', exactMatch.text);
    return exactMatch;
  }

  // ✅ STEP 3: Try matching by keywords (longer phrases)
  const keywords = query.split(/\s+/).filter(w => w.length > 2);
  if (keywords.length > 0) {
    const keywordMatch = allEvents.find(e => {
      const eventTitle = e.text.toLowerCase();
      // Match if ALL keywords are in title
      return keywords.every(kw => eventTitle.includes(kw));
    });
    
    if (keywordMatch) {
      console.log('✅ Found keyword match:', keywordMatch.text);
      return keywordMatch;
    }
  }

  // ✅ STEP 4: Try matching by day of week
  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const foundDay = days.find(day => query.includes(day));
  
  if (foundDay) {
    const dayMatches = allEvents.filter(e => e.dayOfWeek === foundDay);
    
    if (dayMatches.length === 1) {
      console.log('✅ Found by day:', dayMatches[0].text);
      return dayMatches[0];
    } else if (dayMatches.length > 1) {
      // Try to narrow by time
      const timeMatch = query.match(/(\d{1,2}):?(\d{2})?\s*(am|pm)?/i);
      if (timeMatch) {
        const queryTime = this.parseTimeToMinutes(
          timeMatch[3] 
            ? `${timeMatch[1]}:${timeMatch[2] || '00'} ${timeMatch[3]}`
            : `${timeMatch[1]}:${timeMatch[2] || '00'}`
        );
        
        if (queryTime !== null) {
          const match = dayMatches.find(e => 
            e.timeMinutes && Math.abs(e.timeMinutes - queryTime) < 30
          );
          if (match) {
            console.log('✅ Found by day + time:', match.text);
            return match;
          }
        }
      }
      
      // Return first if can't narrow
      console.log('✅ Returning first match for day:', dayMatches[0].text);
      return dayMatches[0];
    }
  }

  // ✅ STEP 5: Try matching by time alone
  const timeMatch = query.match(/(\d{1,2}):?(\d{2})?\s*(am|pm)?/i);
  if (timeMatch) {
    const queryTime = this.parseTimeToMinutes(
      timeMatch[3] 
        ? `${timeMatch[1]}:${timeMatch[2] || '00'} ${timeMatch[3]}`
        : `${timeMatch[1]}:${timeMatch[2] || '00'}`
    );
    
    if (queryTime !== null) {
      const timeOnlyMatch = allEvents.find(e => 
        e.timeMinutes && Math.abs(e.timeMinutes - queryTime) < 30
      );
      
      if (timeOnlyMatch) {
        console.log('✅ Found by time:', timeOnlyMatch.text);
        return timeOnlyMatch;
      }
    }
  }

  console.log('❌ No event found matching:', query);
  return null;
}

  parseTimeToMinutes(timeStr) {
    if (!timeStr || timeStr === 'All day') return null;
    
    const match = timeStr.match(/(\d{1,2}):?(\d{2})?\s*(am|pm)?/i);
    if (!match) return null;
    
    let hours = parseInt(match[1]);
    const minutes = match[2] ? parseInt(match[2]) : 0;
    const meridian = match[3]?.toUpperCase();
    
    if (meridian === 'PM' && hours !== 12) hours += 12;
    if (meridian === 'AM' && hours === 12) hours = 0;
    
    return hours * 60 + minutes;
  }

  clearHistory() {
    this.messageHistory = [];
    this.pendingToolCall = false;
    this.lastConfirmedAction = null;
    console.log('🧹 History cleared');
  }
  async getAzureResponseWithoutTools() {
  try {
    const systemPrompt = `You are Preptune. Give a brief, natural confirmation. ${this.userName ? `User's name is ${this.userName}.` : ''} Max 20 words. No exclamation marks. Use natural time format like "2pm" or "3:30pm".`;

    const messages = [
      { role: "system", content: systemPrompt },
      ...this.messageHistory
    ];

    const response = await SecureAPI.callAzureOpenAI(messages, {
      temperature: 0.8,
      max_tokens: 50
    });

    return response;

  } catch (error) {
    console.error('❌ Azure error:', error);
    throw error;
  }
}
}

export default BigCalendarPlannerChatManager;